import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Manages the columns popover (list of visible/hidden columns + freeze count).
// On Apply: encodes the new column order + frozen_count into URL params and
// hands off to etudes-index for the actual navigation.
export default class extends Controller {
  static targets = ["visibleList", "hiddenList"]
  static values = { frozenCount: Number }

  connect() {
    if (this.hasVisibleListTarget) {
      this.sortable = Sortable.create(this.visibleListTarget, {
        animation: 150,
        handle: ".columns-picker__handle",
        // Required (ref_etude) item is filtered out of drag — it stays first.
        filter: ".is-required",
        onMove: (evt) => {
          // Don't allow dropping above the required item
          if (evt.related.classList.contains("is-required")) return false
          return true
        },
        onEnd: () => this._refreshFrozenStateFromDOM()
      })
    }
  }

  disconnect() { this.sortable?.destroy() }

  // ---- actions -----------------------------------------------------------

  toggleFreeze(event) {
    event.stopPropagation()
    const idx = parseInt(event.currentTarget.dataset.index, 10) // 0-based position
    const newCount = idx + 1                                    // freeze through this one
    // If already frozen at this point, "unfreeze" — drop back to 1 (only ref_etude)
    if (this.frozenCountValue === newCount) {
      this.frozenCountValue = 1
    } else {
      this.frozenCountValue = newCount
    }
    this._refreshFrozenStateFromDOM()
  }

  hide(event) {
    event.stopPropagation()
    const item = event.currentTarget.closest(".columns-picker__item")
    if (!item) return
    const key = item.dataset.key
    if (!key) return
    item.remove()
    // Re-render the hidden list inline by appending a placeholder
    if (!this.hasHiddenListTarget) {
      // Create the hidden list container if it doesn't exist yet
      const ul = document.createElement("ul")
      ul.className = "columns-picker__list columns-picker__list--hidden"
      ul.setAttribute("data-columns-picker-target", "hiddenList")
      this.element.querySelector(".columns-picker__menu").insertBefore(ul, this.element.querySelector(".columns-picker__footer"))
    }
    const li = document.createElement("li")
    li.className = "columns-picker__item is-hidden"
    li.dataset.key = key
    li.innerHTML = `
      <i class="fa-solid fa-eye-slash columns-picker__handle" aria-hidden="true" style="opacity:0.4;"></i>
      <span class="columns-picker__name">${this._labelForKey(key)}</span>
      <button type="button" class="columns-picker__show"
              data-action="click->columns-picker#show"
              title="Afficher"
              aria-label="Afficher"><i class="fa-solid fa-plus"></i></button>
    `
    this.hiddenListTarget.appendChild(li)
    this._refreshFrozenStateFromDOM()
  }

  show(event) {
    event.stopPropagation()
    const item = event.currentTarget.closest(".columns-picker__item")
    if (!item) return
    const key = item.dataset.key
    item.remove()

    const li = document.createElement("li")
    li.className = "columns-picker__item"
    li.dataset.key = key
    li.innerHTML = `
      <i class="fa-solid fa-grip-vertical columns-picker__handle" aria-hidden="true"></i>
      <span class="columns-picker__name">${this._labelForKey(key)}</span>
      <button type="button" class="columns-picker__freeze-toggle"
              data-action="click->columns-picker#toggleFreeze"
              data-index="0"
              title="Figer jusqu'à ici"><i class="fa-solid fa-thumbtack"></i></button>
      <button type="button" class="columns-picker__hide"
              data-action="click->columns-picker#hide"
              title="Masquer"><i class="fa-solid fa-eye-slash"></i></button>
    `
    this.visibleListTarget.appendChild(li)
    this._refreshFrozenStateFromDOM()
  }

  reset() {
    if (!confirm("Réinitialiser les colonnes par défaut ?")) return
    const idx = window.__listIndex || window.__etudesIndex
    if (idx) {
      idx.navigateWith({ "columns": null, "frozen_count": null })
    }
  }

  apply() {
    const items = Array.from(this.visibleListTarget.querySelectorAll(".columns-picker__item"))
    const orderedKeys = items.map(li => li.dataset.key)
    // Required columns must stay leftmost (in their declared order).
    const requiredKeys = items.filter(li => li.dataset.required === "true").map(li => li.dataset.key)
    requiredKeys.slice().reverse().forEach(req => {
      const i = orderedKeys.indexOf(req)
      if (i > 0) { orderedKeys.splice(i, 1); orderedKeys.unshift(req) }
    })

    const frozen = Math.max(1, Math.min(this.frozenCountValue, orderedKeys.length))

    const idx = window.__listIndex || window.__etudesIndex
    if (!idx) {
      console.warn("list-index controller not connected")
      return
    }

    const params = idx._currentParams()
    // Strip existing column params then write the new order.
    Object.keys(params).forEach(k => {
      if (k === "frozen_count" || k.startsWith("columns[")) delete params[k]
    })
    params["columns[]"] = orderedKeys
    params["frozen_count"] = frozen
    idx._navigate(params)
  }

  // ---- helpers -----------------------------------------------------------

  _refreshFrozenStateFromDOM() {
    const items = Array.from(this.visibleListTarget.querySelectorAll(".columns-picker__item"))
    items.forEach((li, idx) => {
      const isFrozen = idx < this.frozenCountValue
      li.classList.toggle("is-frozen", isFrozen)
      const btn = li.querySelector(".columns-picker__freeze-toggle")
      if (btn) {
        btn.classList.toggle("is-active", isFrozen)
        btn.dataset.index = idx
      }
    })
  }

  _labelForKey(key) {
    // Look up label in the schema embedded by the page (list-index config)
    const cfg = (window.__listIndex || window.__etudesIndex)?.configValue
    const all = cfg?.columns_schema || []
    const found = all.find(c => c.key === key)
    return found?.label || key
  }
}
